import { assign, createMachine } from 'xstate';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { Credentials, CredentialsEntity, CredentialsStatus } from '../../models/credentials';
import {
  AUTH_CREDENTIALS_FAILED,
  CREATE_CREDENTIALS_FAILED,
  CredentialsService,
  credentialsService as credentialsServiceImport,
  REFRESH_CREDENTIALS_FAILED,
  UPDATE_CREDENTIALS_FAILED,
} from '../services/credentialsService';
import { containsCredentials, tokenIsValid } from '../services/credentials';
import { handoffFlowAvailable, ProviderAccessType, ProviderEntity } from '../../models/provider';
import { RefreshableItem } from '../../models/refreshableItem';
import { ProductNames } from '../../models/product';
import { getCredentialsAppUri } from '../../utilities/app-uri/app-uri';
import {
  navigateUserToInitialTinkLinkAfterThirdPartyRedirect,
  shouldRedirectToReferPage,
  shouldSkipUserConfirmation as shouldSkipUserConfirmationImport,
} from '../../models/configuration';
import { applicationParameters } from '../../utilities/application-parameters';
import { AuthenticationOption, CredentialsRequest } from '../services/credentialsClient';
import { CommonError } from '../../stores/app-state-errors';
import { FETCHING_CREDENTIALS_FAILED } from '../../utilities/error';
import {
  ERROR_PROVIDER,
  ProvidersService,
  providersService as providersServiceImport,
} from '../../services/providersService';
import { ProviderConsentService, providerConsentService } from '../services/providerConsent';

export type LoginMachineContext = {
  operation:
    | 'createCredentials'
    | 'addExistingCredentials'
    | 'authenticateExistingCredentials'
    | 'refreshExistingCredentials'
    | 'updateConsent'
    | 'extendConsent';
  provider: ProviderEntity;
  productNames: ProductNames[];
  configurationData: ConfigurationModel;
  userId: string;
  refreshableItems: RefreshableItem[];
  credentials: CredentialsEntity | undefined;
  userCredentials: CredentialsEntity<CredentialsStatus>[];
  existingUserCredentials?: CredentialsEntity<CredentialsStatus>;
  selectedAuthenticationOptions?: AuthenticationOption[];
  credentialsId?: string;
  fields?: Record<string, string>;
  error?: CommonError;
};

export type LoginMachineParams = {
  context: LoginMachineContext;
  initialState?: string;
  credentialsService?: CredentialsService;
  providersService?: ProvidersService;
  providerConsentService?: ProviderConsentService;
  callbackUri?: string;
};

type LoginMachineServices = {
  updateCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  refreshCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  createCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  getExistingCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  authenticateExistingCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  refreshExistingCredentials: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  getProvider: {
    data: ProviderEntity;
  };
  updateConsent: {
    data: CredentialsEntity<CredentialsStatus>;
  };
  extendConsent: {
    data: CredentialsEntity<CredentialsStatus>;
  };
};

type LoginMachineEvents =
  | {
      type: 'EVENT_SUBMIT_CREDENTIALS';
      fields: Record<string, string>;
    }
  | {
      type: 'EVENT_BACK';
      fields: undefined;
    };

export const createLoginMachine = ({
  context,
  initialState = 'decideIfExistingCredentialsFlow',
  credentialsService = credentialsServiceImport,
  providersService = providersServiceImport,
  callbackUri,
}: LoginMachineParams) =>
  createMachine(
    {
      predictableActionArguments: true,
      schema: {
        context: {} as LoginMachineContext,
        services: {} as LoginMachineServices,
        events: {} as LoginMachineEvents,
      },
      tsTypes: {} as import('./loginMachine.typegen').Typegen0,
      id: 'login-machine',
      initial: initialState,
      context,
      states: {
        decideIfExistingCredentialsFlow: {
          always: [
            {
              target: 'uiCreateOrRefreshCredentials',
              cond: 'isAddExistingCredentialsFlow',
            },
            {
              target: 'processGetExistingCredentialsForAuthentication',
              cond: 'isAuthenticateExistingCredentialsFlow',
            },
            {
              target: 'processGetExistingCredentialsForRefresh',
              cond: 'isRefreshExistingCredentialsFlow',
            },
            {
              target: 'processGetExistingCredentialsForUpdateConsent',
              cond: 'isUpdateConsentFlow',
            },
            {
              target: 'processGetExistingCredentialsForExtendConsent',
              cond: 'isExtendConsentFlow',
            },
            {
              target: 'decideIfUpdate',
            },
          ],
        },
        processGetExistingCredentialsForAuthentication: {
          invoke: {
            src: 'getExistingCredentials',
            onDone: {
              target: 'decideIfAutoAuthenticate',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setFetchCredentialsError',
            },
          },
        },
        processGetExistingCredentialsForRefresh: {
          invoke: {
            src: 'getExistingCredentials',
            onDone: {
              target: 'processGetProviderForRefresh',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setFetchCredentialsError',
            },
          },
        },
        processGetExistingCredentialsForUpdateConsent: {
          invoke: {
            src: 'getExistingCredentials',
            onDone: {
              target: 'processGetProviderForUpdateConsent',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setFetchCredentialsError',
            },
          },
        },
        processGetExistingCredentialsForExtendConsent: {
          invoke: {
            src: 'getExistingCredentials',
            onDone: {
              target: 'processGetProviderForExtendConsent',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setFetchCredentialsError',
            },
          },
        },
        processGetProviderForRefresh: {
          invoke: {
            src: 'getProvider',
            onDone: {
              target: 'decideIfCredentialsRefreshLimitReached',
              actions: 'setProvider',
            },
            onError: {
              target: 'processError',
              actions: 'setGetProviderError',
            },
          },
        },
        processGetProviderForUpdateConsent: {
          invoke: {
            src: 'getProvider',
            onDone: {
              target: 'decideIfCredentialsRefreshLimitReachedForUpdateConsent',
              actions: 'setProvider',
            },
            onError: {
              target: 'processError',
              actions: 'setGetProviderError',
            },
          },
        },
        processGetProviderForExtendConsent: {
          invoke: {
            src: 'getProvider',
            onDone: {
              target: 'decideIfAutoExtendConsent',
              actions: 'setProvider',
            },
            onError: {
              target: 'processError',
              actions: 'setGetProviderError',
            },
          },
        },
        decideIfCredentialsRefreshLimitReached: {
          always: [
            {
              target: 'decideIfAutoRefresh',
            },
          ],
        },
        decideIfCredentialsRefreshLimitReachedForUpdateConsent: {
          always: [
            {
              target: 'decideIfAutoUpdateConsent',
            },
          ],
        },
        decideIfAutoAuthenticate: {
          always: [
            {
              target: 'processAuthenticateExistingCredentials',
              cond: 'shouldAutoAuthenticate',
            },
            {
              target: 'uiAuthenticateExistingCredentials',
            },
          ],
        },
        decideIfAutoRefresh: {
          always: [
            {
              target: 'processRefreshExistingCredentials',
              cond: 'shouldAutoRefresh',
            },
            {
              target: 'uiRefreshExistingCredentials',
            },
          ],
        },
        decideIfAutoUpdateConsent: {
          always: [
            {
              target: 'processUpdateConsent',
              cond: 'shouldAutoUpdateConsent',
            },
            {
              target: 'uiUpdateConsent',
            },
          ],
        },
        decideIfAutoExtendConsent: {
          always: [
            {
              target: 'processExtendConsent',
              cond: 'shouldAutoUpdateConsent',
            },
            {
              target: 'uiExtendConsent',
            },
          ],
        },
        decideIfUpdate: {
          always: [
            {
              target: 'uiUpdateCredentials',
              cond: 'hasCredentialsError',
              description:
                'Credentials are in error state and should be updated with new user input',
            },
            {
              target: 'uiCreateOrRefreshCredentials',
            },
          ],
        },
        uiCreateOrRefreshCredentials: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'decideIfRefreshOrCreate',
              actions: ['setFields', 'setExistingUserCredentials'],
            },
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        decideIfRefreshOrCreate: {
          always: [
            {
              target: 'uiPollingRefreshCredentials',
              cond: 'hasExistingUserCredentials',
              description: 'Resubmitting credentials with the same field values.',
            },
            {
              target: 'uiPollingCreateCredentials',
            },
          ],
        },
        uiUpdateCredentials: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'uiPollingUpdateCredentials',
              actions: 'setFields',
            },
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiAuthenticateExistingCredentials: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'processAuthenticateExistingCredentials',
            },
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiRefreshExistingCredentials: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'processRefreshExistingCredentials',
            },
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiUpdateConsent: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'processUpdateConsent',
            },
          },
        },
        uiExtendConsent: {
          on: {
            EVENT_SUBMIT_CREDENTIALS: {
              target: 'processExtendConsent',
            },
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiPollingUpdateCredentials: {
          invoke: {
            src: 'updateCredentials',
            onDone: {
              target: 'decideIfUpdateCredentialsDone',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setUpdateCredentialsError',
            },
          },
          on: {
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiPollingRefreshCredentials: {
          invoke: {
            src: 'refreshCredentials',
            onDone: {
              target: 'decideIfRefreshCredentialsDone',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setRefreshCredentialsError',
            },
          },
          on: {
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        uiPollingCreateCredentials: {
          invoke: {
            src: 'createCredentials',
            onDone: {
              target: 'decideIfCreateCredentialsDone',
              actions: 'setCredentials',
            },
            onError: {
              target: 'processError',
              actions: 'setCreateCredentialsError',
            },
          },
          on: {
            EVENT_BACK: {
              target: 'processBack',
            },
          },
        },
        processAuthenticateExistingCredentials: {
          invoke: {
            src: 'authenticateExistingCredentials',
            onDone: {
              target: 'decideIfAuthenticateExistingCredentialsDone',
            },
            onError: {
              target: 'processError',
              actions: 'setAuthenticateUserCredentialsError',
            },
          },
        },
        processRefreshExistingCredentials: {
          invoke: {
            src: 'refreshExistingCredentials',
            onDone: {
              target: 'decideIfRefreshExistingCredentialsDone',
            },
            onError: {
              target: 'processError',
              actions: 'setRefreshCredentialsError',
            },
          },
        },
        processUpdateConsent: {
          invoke: {
            src: 'updateConsent',
            onDone: {
              target: 'decideIfUpdateConsentDone',
            },
            onError: {
              target: 'processError',
              actions: 'setUpdateConsentError',
            },
          },
        },
        processExtendConsent: {
          invoke: {
            src: 'extendConsent',
            onDone: {
              target: 'decideIfExtendConsentDone',
            },
            onError: {
              target: 'processError',
              actions: 'setExtendConsentError',
            },
          },
        },
        decideIfUpdateCredentialsDone: {
          always: [
            {
              target: 'uiUpdateCredentials',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processUpdateCredentialsDone',
            },
          ],
        },
        decideIfRefreshCredentialsDone: {
          always: [
            {
              target: 'uiUpdateCredentials',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processRefreshCredentialsDone',
            },
          ],
        },
        decideIfCreateCredentialsDone: {
          always: [
            {
              target: 'uiUpdateCredentials',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processCreateCredentialsDone',
            },
          ],
        },
        decideIfAuthenticateExistingCredentialsDone: {
          always: [
            {
              target: 'processError',
              actions: 'setAuthenticateUserCredentialsError',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processAuthenticateExistingCredentialsDone',
            },
          ],
        },
        decideIfRefreshExistingCredentialsDone: {
          always: [
            {
              target: 'processError',
              actions: 'setRefreshCredentialsError',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processRefreshExistingCredentialsDone',
            },
          ],
        },
        decideIfUpdateConsentDone: {
          always: [
            {
              target: 'processError',
              actions: 'setUpdateConsentError',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processUpdateConsentDone',
            },
          ],
        },
        decideIfExtendConsentDone: {
          always: [
            {
              target: 'processError',
              actions: 'setExtendConsentError',
              cond: 'hasCredentialsError',
            },
            {
              target: 'processExtendConsentDone',
            },
          ],
        },
        processUpdateCredentialsDone: {
          type: 'final',
        },
        processCreateCredentialsDone: {
          type: 'final',
        },
        processRefreshCredentialsDone: {
          type: 'final',
        },
        processAuthenticateExistingCredentialsDone: {
          type: 'final',
        },
        processRefreshExistingCredentialsDone: {
          type: 'final',
        },
        processUpdateConsentDone: {
          type: 'final',
        },
        processExtendConsentDone: {
          type: 'final',
        },
        processError: {
          type: 'final',
        },
        processBack: {
          type: 'final',
        },
      },
    },
    {
      services: {
        updateCredentials: async ({
          provider,
          credentials,
          configurationData,
          productNames,
          selectedAuthenticationOptions,
          fields,
        }) => {
          const credentialsRequest: CredentialsRequest = {
            fields: fields ?? {},
            appUri: getCredentialsAppUri({
              redirectToReferPage: shouldRedirectToReferPage(configurationData),
              navigateToInitialTinkLinkRedirect:
                navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
            }),
            providerName: provider.name,
            callbackUri,
            productNames,
            selectedAuthenticationOptions,
          };

          const result = await credentialsService.updateCredentials(
            credentials!.id,
            credentialsRequest
          );

          return result;
        },
        refreshCredentials: async ({
          credentials,
          configurationData,
          refreshableItems,
          productNames,
        }) => {
          await credentialsService.refreshCredentials({
            id: credentials!.id,
            authenticate: applicationParameters.get('authenticate'),
            request: {
              appUri: getCredentialsAppUri({
                redirectToReferPage: shouldRedirectToReferPage(configurationData),
                navigateToInitialTinkLinkRedirect:
                  navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
              }),
              callbackUri,
              productNames,
            },
            refreshableItems,
          });

          return credentials as CredentialsEntity<CredentialsStatus>;
        },
        updateConsent: async ({
          credentialsId,
          existingUserCredentials,
          configurationData,
          productNames,
          refreshableItems,
        }) => {
          await credentialsService.refreshCredentials({
            id: credentialsId!,
            authenticate: true,
            request: {
              appUri: getCredentialsAppUri({
                redirectToReferPage: shouldRedirectToReferPage(configurationData),
                navigateToInitialTinkLinkRedirect:
                  navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
              }),
              productNames,
            },
            refreshableItems,
          });

          return existingUserCredentials as CredentialsEntity<CredentialsStatus>;
        },
        extendConsent: async ({ credentialsId, credentials }) => {
          await providerConsentService.extendConsent(credentialsId!);

          return credentials as CredentialsEntity<CredentialsStatus>;
        },
        createCredentials: async ({
          provider,
          configurationData,
          refreshableItems,
          productNames,
          selectedAuthenticationOptions,
          fields,
        }) => {
          const credentialsRequest: CredentialsRequest = {
            fields: fields ?? {},
            appUri: getCredentialsAppUri({
              redirectToReferPage: shouldRedirectToReferPage(configurationData),
              navigateToInitialTinkLinkRedirect:
                navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
            }),
            providerName: provider.name,
            callbackUri,
            productNames,
            selectedAuthenticationOptions,
          };

          const result = await credentialsService.createCredentials(
            credentialsRequest,
            refreshableItems
          );

          return result;
        },
        getExistingCredentials: async ({ credentialsId }) => {
          const credentials = await credentialsService.getCredentials(credentialsId!);

          return credentials;
        },
        authenticateExistingCredentials: async ({
          credentials,
          configurationData,
          productNames,
        }) => {
          await credentialsService.authenticateCredentials({
            id: credentials!.id,
            request: {
              appUri: getCredentialsAppUri({
                redirectToReferPage: shouldRedirectToReferPage(configurationData),
                navigateToInitialTinkLinkRedirect:
                  navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
              }),
              productNames,
            },
          });

          return credentials!;
        },
        refreshExistingCredentials: async ({
          credentials,
          configurationData,
          productNames,
          refreshableItems,
        }) => {
          await credentialsService.refreshCredentials({
            id: credentials!.id,
            authenticate: Boolean(applicationParameters.get('authenticate')),
            request: {
              appUri: getCredentialsAppUri({
                redirectToReferPage: shouldRedirectToReferPage(configurationData),
                navigateToInitialTinkLinkRedirect:
                  navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
              }),
              productNames,
            },
            refreshableItems,
          });

          return credentials!;
        },
        getProvider: async ({ credentials }) => {
          const provider = await providersService.fetchProvider(credentials!.providerName);

          return provider;
        },
      },
      guards: {
        hasCredentialsError: ({ credentials }) =>
          Boolean(credentials) && Credentials.hasError(credentials),
        hasExistingUserCredentials: ({ existingUserCredentials }) =>
          Boolean(existingUserCredentials),
        shouldAutoAuthenticate: ({ configurationData, provider, userId }) =>
          shouldSkipUserConfirmationImport(configurationData) &&
          !handoffFlowAvailable(provider, configurationData, userId),
        isAddExistingCredentialsFlow: ({ operation }) => operation === 'addExistingCredentials',
        isAuthenticateExistingCredentialsFlow: ({ operation }) =>
          operation === 'authenticateExistingCredentials',
        isRefreshExistingCredentialsFlow: ({ operation }) =>
          operation === 'refreshExistingCredentials',
        shouldAutoRefresh: ({ credentials, provider, configurationData, userId }) => {
          const tokenIsFresh =
            provider.accessType === ProviderAccessType.OPEN_BANKING &&
            tokenIsValid(credentials!) &&
            !applicationParameters.get('authenticate');

          return (
            tokenIsFresh ||
            (shouldSkipUserConfirmationImport(configurationData) &&
              !handoffFlowAvailable(provider, configurationData, userId))
          );
        },
        isUpdateConsentFlow: ({ operation }) => operation === 'updateConsent',
        shouldAutoUpdateConsent: ({ provider, configurationData, userId }) =>
          shouldSkipUserConfirmationImport(configurationData) &&
          !handoffFlowAvailable(provider, configurationData, userId),
        isExtendConsentFlow: ({ operation }) => operation === 'extendConsent',
      },
      actions: {
        setFields: assign({
          fields: (_, event) => event.fields,
        }),
        setCredentials: assign({
          credentials: (_, event) => event.data as CredentialsEntity<CredentialsStatus>,
        }),
        setProvider: assign({
          provider: (_, event) => event.data as ProviderEntity,
        }),
        setUpdateCredentialsError: assign({
          error: (_) => UPDATE_CREDENTIALS_FAILED,
        }),
        setRefreshCredentialsError: assign({
          error: (_) => REFRESH_CREDENTIALS_FAILED,
        }),
        setCreateCredentialsError: assign({
          error: (_) => CREATE_CREDENTIALS_FAILED,
        }),
        setFetchCredentialsError: assign({
          error: (_) => FETCHING_CREDENTIALS_FAILED,
        }),
        setAuthenticateUserCredentialsError: assign({
          error: (_) => AUTH_CREDENTIALS_FAILED,
        }),
        setGetProviderError: assign({
          error: (_) => ERROR_PROVIDER,
        }),
        setUpdateConsentError: assign({
          error: (_) => REFRESH_CREDENTIALS_FAILED,
        }),
        setExtendConsentError: assign({
          error: (_) => REFRESH_CREDENTIALS_FAILED,
        }),
        setExistingUserCredentials: assign({
          existingUserCredentials: (
            {
              provider,
              productNames,
              configurationData,
              selectedAuthenticationOptions,
              userCredentials,
            },
            event
          ) => {
            const credentialsRequest: CredentialsRequest = {
              fields: event.fields ?? {},
              appUri: getCredentialsAppUri({
                redirectToReferPage: shouldRedirectToReferPage(configurationData),
                navigateToInitialTinkLinkRedirect:
                  navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
              }),
              providerName: provider.name,
              callbackUri,
              productNames,
              selectedAuthenticationOptions,
            };

            const existingUserCredentials = containsCredentials(
              userCredentials,
              credentialsRequest
            );

            return existingUserCredentials;
          },
        }),
      },
    }
  );
